(ns frontend.ui.ai-summary-configuration.editor
  "Reusable content, source, style, and template editor."
  (:require
   [common.ai.deal-one-pager-sources :as sources]
   [common.localization :as loc]
   [common.schema.entities.ai-summary-configuration.options :as options]
   [ui.components.button.view :as button]
   [ui.components.input.file-input :as file-input]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn- document-control
  [{:keys [field filename uploading? disabled? on-upload on-clear]}]
  [:div
   {:class "flex flex-col gap-1.5"}
   [:div
    {:class "text-[14px] font-semibold text-input-light-text"}
    (loc/tr (case field
              :style-guideline-document
              :deal-one-pager/style-guideline-document
              :deal-one-pager/document-template))]
   [:div
    {:class "flex flex-wrap items-center gap-2 rounded border border-base-light-border-paler p-2"}
    [:span
     {:class "min-w-0 flex-1 truncate text-sm text-base-light-text"}
     (or filename (loc/tr :deal-one-pager/no-document-selected))]
    [file-input/view
     {:accept-file-types (case field
                           :style-guideline-document [:pdf :doc :docx :txt]
                           :document-template [:docx])
      :on-change (fn [{:keys [files]}]
                   (when-let [file (when-not (or disabled? uploading?)
                                     (first files))]
                     (on-upload field file)))
      :trigger
      [:span
       {:class (if disabled?
                 "cursor-not-allowed text-sm font-semibold text-disabled-regular-text"
                 "cursor-pointer text-sm font-semibold text-base-light-text-accent")}
       (loc/tr (if uploading?
                 :deal-one-pager/uploading-document
                 :deal-one-pager/upload-document))]}]
    (when filename
      [button/sm-secondary-hollow
       {:disabled? (or disabled? uploading?)
        :on-click (fn []
                    (on-clear field))}
       (loc/tr :deal-one-pager/clear-document)])]])

(defn- source-checkboxes
  [{:keys [selected-data-sources disabled? on-toggle-source]}]
  [:div
   {:class "flex flex-col gap-3"}
   [:div
    {:class "text-sm font-semibold text-base-light-text"}
    (loc/tr :deal-one-pager/source-data)]
   [:div
    {:class "grid grid-cols-1 gap-2 xl:grid-cols-3"}
    (for [root-kind [:deal :target :profile]]
      ^{:key root-kind}
      [:div
       {:class "rounded border border-base-light-border-paler p-3"}
       [:div
        {:class "mb-2 font-semibold text-base-light-text"}
        (loc/tr (case root-kind
                  :deal :deal-one-pager/source-root-deal
                  :target :deal-one-pager/source-root-target
                  :deal-one-pager/source-root-profile))]
       [:div
        {:class "flex flex-col gap-2"}
        (for [{:keys [source label-key]}
              (filter (fn [entry]
                        (= root-kind (:root-kind entry)))
                      sources/source-registry)]
          ^{:key source}
          [labelled-input/view
           {:editor-type :editor.type/checkbox
            :horizontal? true
            :reverse? true
            :disabled? disabled?
            :label (loc/tr label-key)
            :value (contains? selected-data-sources source)
            :on-change (fn [selected?]
                         (on-toggle-source source selected?))}])]])]])

(defn view
  [{:keys [draft disabled? uploading-field on-change on-toggle-source
           on-upload on-clear]}]
  (let [style-source (:style-guideline-source draft)
        style-options
        [{:value options/style-guideline-source-none
          :label (loc/tr :deal-one-pager/style-source-none)}
         {:value options/style-guideline-source-document
          :label (loc/tr :deal-one-pager/style-source-document)}
         {:value options/style-guideline-source-url
          :label (loc/tr :deal-one-pager/style-source-url)}]]
    [:div
     {:class "flex flex-col gap-5"}
     [:div
      {:class "grid grid-cols-1 gap-4 md:grid-cols-2"}
      [labelled-input/view
       {:label (loc/tr :deal-one-pager/configuration-name)
        :mandatory? true
        :disabled? disabled?
        :data-type :data.type/text-simple
        :value (or (:configuration-name draft) "")
        :on-change (fn [value]
                     (on-change :configuration-name value))}]
      [labelled-input/view
       {:label (loc/tr :deal-one-pager/configuration-description)
        :disabled? disabled?
        :data-type :data.type/text-simple
        :value (or (:configuration-description draft) "")
        :on-change (fn [value]
                     (on-change :configuration-description value))}]]
     [labelled-input/view
      {:label (loc/tr :deal-one-pager/summary-instructions)
       :mandatory? true
       :disabled? disabled?
       :data-type :data.type/text-multiline
       :class "min-h-40"
       :value (or (:summary-instructions draft) "")
       :on-change (fn [value]
                    (on-change :summary-instructions value))}]
     [source-checkboxes
      {:selected-data-sources (:selected-data-sources draft)
       :disabled? disabled?
       :on-toggle-source on-toggle-source}]
     [:div
      {:class "flex flex-col gap-4 rounded border border-base-light-border-paler p-4"}
      [:div
       {:class "font-semibold text-base-light-text"}
       (loc/tr :deal-one-pager/style-and-template)]
      [labelled-input/view
       {:editor-type :editor.type/select
        :label (loc/tr :deal-one-pager/style-source)
        :disabled? disabled?
        :options style-options
        :value style-source
        :on-change (fn [value _option]
                     (on-change :style-guideline-source value))}]
      (when (= options/style-guideline-source-url style-source)
        [labelled-input/view
         {:label (loc/tr :deal-one-pager/style-reference-url)
          :mandatory? true
          :disabled? disabled?
          :data-type :data.type/text-simple
          :value (or (:style-reference-url draft) "")
          :on-change (fn [value]
                       (on-change :style-reference-url value))}])
      (when (= options/style-guideline-source-document style-source)
        [document-control
         {:field :style-guideline-document
          :filename (:style-guideline-document-filename draft)
          :uploading? (= :style-guideline-document uploading-field)
          :disabled? disabled?
          :on-upload on-upload
          :on-clear on-clear}])
      [document-control
       {:field :document-template
        :filename (:document-template-filename draft)
        :uploading? (= :document-template uploading-field)
        :disabled? disabled?
        :on-upload on-upload
        :on-clear on-clear}]
      [:div
       {:class "text-xs text-base-light-text-paler"}
       (loc/tr :deal-one-pager/word-template-output-hint)]
      [:div
       {:class "text-xs text-base-light-text-paler"}
       (loc/tr :deal-one-pager/pdf-template-fallback)]]]))
